import * as THREE from 'three';
import { createSweaterRig } from './sweaterRig.js';

/**
 * Shared 3D stage — one renderer, one camera, one render loop, for the
 * entire site. The garment rig (sweaterRig.js) is the only visual
 * protagonist; camera and lighting are driven by the single site-wide
 * story progress passed in via setStoryProgress().
 */

function detectWeakDevice() {
  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const smallScreen = window.matchMedia?.('(max-width: 640px)').matches ?? false;
  const fewCores = (navigator.hardwareConcurrency ?? 8) <= 4;
  return (coarsePointer && smallScreen) || fewCores;
}

export function createScene(canvas) {
  const weakDevice = detectWeakDevice();
  // Pixel ratio capped at 2 on desktop, 1.5 on mobile/weak devices.
  const maxPixelRatio = weakDevice ? 1.5 : 2;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxPixelRatio));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = false;

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x0b0a0e, 0.045);

  const camera = new THREE.PerspectiveCamera(
    42,
    window.innerWidth / window.innerHeight,
    0.1,
    100
  );
  camera.position.set(0, 0, 9.4);

  // ---- Lighting — warm studio: soft ambient fill + warm key + cool rim --
  const hemi = new THREE.HemisphereLight(0xf3eee4, 0x0e0d12, 0.65);
  scene.add(hemi);

  const keyLight = new THREE.DirectionalLight(0xfff1d8, 1.8);
  keyLight.position.set(4, 6, 5);
  scene.add(keyLight);

  const rimLight = new THREE.DirectionalLight(0xd8c9a3, 0.7);
  rimLight.position.set(-5, 2, -4);
  scene.add(rimLight);

  const fillLight = new THREE.DirectionalLight(0xcdd8ff, 0.35);
  fillLight.position.set(-2, -3, 4);
  scene.add(fillLight);

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rig = createSweaterRig({ scene, camera, reducedMotion });

  const clock = new THREE.Clock();

  function setStoryProgress(p) {
    rig.setProgress(p);
  }

  function whenLoaded(cb) {
    rig.whenLoaded(cb);
  }

  function resize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxPixelRatio));
    rig.resize();
  }

  function render() {
    const dt = clock.getDelta();
    rig.update(dt);
    renderer.render(scene, camera);
  }

  return {
    renderer,
    scene,
    camera,
    setStoryProgress,
    whenLoaded,
    resize,
    render,
    get loaded() {
      return rig.loaded;
    },
  };
}
